import React, { Fragment } from 'react';
import { messageFormat as message } from '@clubmed/utils';
import { remove } from 'Client/local-storage-api';
import { removeFromSessionStorage } from 'Client/session-storage-api/sessionStorageApi';
import { KEY_CA_VOUCHER_TOASTER } from 'Client/session-storage-api/sessionStorage.constants';
import { useSearchBookings } from 'Components/utils/booking/useSearchBookings';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { urlPattern } from 'Routes/routeBuilder';
import { t } from 'Src/i18n';
import signoutAction from 'Store/user-info/actions/signout';
import { CA_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import * as pleUtils from 'Utils/ple-utils';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';

const formatWelcomeMessage = (template, firstName, lastName) =>
  message(template).format({
    firstname: firstName || '',
    lastname: lastName || '',
  });

type Props = {
  closeMemberPanel?: () => void;
  userInfo: UserInfoSummary;
};
type Item = {
  icon: IconName;
  isExternalLink?: boolean;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  url: string | null | undefined;
};
export const LoggedNav = ({ closeMemberPanel, userInfo }: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const appContext = useAppContext();
  const isGsa = isFeatureFlipped(appContext)('isGsa');

  const signoutUrl = pleUtils.buildSignoutUrl({
    shouldRedirectToOidc: isGsa,
    locale: appContext.locale,
  });

  const { favorites, prebookings } = useSearchBookings();
  const items = [
    {
      icon: ICONS.NOTEBOOK,
      label: t('account.authenticated.dashboard'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_dashboard',
        });
      },
      url: urlPattern('account'),
    },
    {
      icon: ICONS.DETAILS,
      label: t('account.authenticated.myTrips'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_myTrips',
        });
      },
      url: urlPattern('stays'),
    },
    {
      icon: ICONS.STAR_HOLLOW,
      label: t('customerAccount.dashboard.travelPlans.title'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_travelplans',
        });
      },
      url: urlPattern('searches'),
      disabled: !prebookings?.length && !favorites?.length,
    },
    {
      icon: ICONS.PEOPLE,
      label: t('account.authenticated.myProfile'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_myProfile',
        });
      },
      url: urlPattern('profile'),
    },
    {
      icon: ICONS.BAR,
      label: t('account.authenticated.greatMembers'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_greatMembersProgram',
        });
      },
      url: urlPattern('greatMembers'),
    },
    {
      icon: ICONS.DISCONNECTION,
      isExternalLink: true,
      label: t('account.authentication.signout'),
      onClick: () => {
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_logout',
        });
        clearAllCacheFront();
        signoutAction();
        closeMemberPanel && closeMemberPanel();
        removeFromSessionStorage(KEY_CA_VOUCHER_TOASTER);
        remove(CA_TRACKING);
      },
      url: signoutUrl,
    },
  ];
  const itemClasses =
    'flex items-center font-bold leading-tight text-black text-16 p-10 air360-replay-ignore';
  return (
    <Fragment>
      <div data-cs-mask className={itemClasses}>
        {formatWelcomeMessage(
          t('account.authenticated.greeting'),
          userInfo.firstName,
          userInfo.lastName,
        )}
      </div>
      <nav>
        {items
          .filter(({ disabled = false }) => !disabled)
          .map(({ icon, isExternalLink, label, onClick, url }: Item) => (
            <DynamicLink
              className={classnames(
                itemClasses,
                'border-t-1 transition-color hocus:text-black-active',
              )}
              isExternalLink={isExternalLink}
              key={url}
              queryParamsToIgnore={[
                'coderesort',
                'hasSaveOption',
                'selectedProposalId',
                'proposalIds',
                'optionIds',
              ]}
              onClick={onClick}
              url={url}
            >
              {icon && <Icon className="me-10" name={icon} width="1.25rem" />}
              {label}
            </DynamicLink>
          ))}
      </nav>
    </Fragment>
  );
};
